import { Button } from "@portal/BaseComponents/Button/AntButton";
import { ScrollDrawer } from "@portal/BaseComponents/Drawer/ScrollDrawer";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { HideWrapper } from "@portal/BaseComponents/HideWrapper/HideWrapper";
import { PopConfirm } from "@portal/BaseComponents/PopConfirm/AntPopConfirm";
import { PopConfirmContent } from "@portal/BaseComponents/PopConfirm/PopConfirmContent";
import { SkeletonInput } from "@portal/BaseComponents/Skeleton/AntSkeleton";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { getPTPStatusConfig } from "@portal/Components/Portal/PotalTabs/PTPs/Columns/predicate";
import { usePrivileges } from "@portal/hooks/use-privilleges";
import useOpen from "@portal/hooks/useOpen";
import { Icon } from "@portal/lib/icons/icons";
import { AppPageParams } from "@portal/lib/router/constants/page-info";
import { usePageNavigate } from "@portal/lib/router/hooks/usePageNavigae";
import {
  useDeletePTPQuery,
  useGetPTPDetailsQuery,
} from "@portal/queries/ptp-query";
import { StatusTag } from "@sinecycle/growcomponents";
import { Fragment, useMemo } from "react";
import { useParams } from "react-router-dom";
import { PTPStatusSelect } from "../PTPStatusSelect/PTPStatusSelect";
import { PTPDetailsComp } from "./Details/PTPDetailsDetails";
import { PTPDetailsViewProps } from "./type";
import { DRAWER_ZINDEX } from "@portal/BaseComponents/Drawer/predicate";

const PTPIcon = (
  <Icon icon={["far", "handshake-simple"]} color="var(--cyan-6)" />
);

const DeleteIcon = <Icon icon={["far", "trash-alt"]} />;
const t = {
  title: "Promise to Pay",
};
function PTPDetailsView(props: PTPDetailsViewProps) {
  const {
    id,
    open,
    header,
    headerTitle = t.title,
    actionButton,
    footer,
    onClose,
  } = props;

  const { open: commentOpen } = useOpen({ open: false });
  const { getPTPPrivilege, current_user } = usePrivileges();
  const { data, isLoading } = useGetPTPDetailsQuery(id);
  const deletePtp = useDeletePTPQuery({ onClose: handleClose });
  const navigate = usePageNavigate();
  const { customer_id } = useParams<AppPageParams>();

  const Content = (
    <PopConfirmContent
      title="Confirm Delete"
      desc="Do you want to delete this PTP?"
      descStyles={{ maxWidth: "160px" }}
    />
  );
  function getDeletePrivilege() {
    return (
      getPTPPrivilege("PTP_DELETE") &&
      current_user === data?.created_by?.id &&
      data?.source_app === "CUSTOMER_PORTAL"
    );
  }

  const deletePrivilege = useMemo(getDeletePrivilege, [
    current_user,
    data?.created_by?.id,
    data?.source_app,
    getPTPPrivilege,
  ]);

  function handleDelete() {
    deletePtp.mutateAsync();
  }
  function handleClose() {
    navigate({
      appPage: "PTPs",
      pathParams: {
        customer_id: customer_id as string,
      },
    });
    onClose?.();
  }

  const DeleteButton = (
    <HideWrapper hide={!deletePrivilege}>
      <PopConfirm
        title={Content}
        onConfirm={handleDelete}
        cancelButtonProps={{
          type: "text",
        }}
        icon={false}
        placement="bottomLeft"
        okText="Delete"
      >
        <Button icon={DeleteIcon} loading={deletePtp.isLoading} />
      </PopConfirm>
    </HideWrapper>
  );
  const HeaderTitle = (
    <>
      {isLoading ? (
        <SkeletonInput />
      ) : (
        <Flex align="center" justify="center" space="8">
          {PTPIcon}
          <TypographyText strong fs="16">
            {`${headerTitle} `}{" "}
            <TypographyText style={{ fontWeight: "var(--fs-regular)" }}>
              ({data?.id})
            </TypographyText>
          </TypographyText>
        </Flex>
      )}
    </>
  );

  const StatusSelect = (
    <HideWrapper hide={!getPTPPrivilege("PTP_UPDATE")}>
      <PTPStatusSelect value={data?.status} />
    </HideWrapper>
  );
  const Status = data ? (
    <HideWrapper hide={getPTPPrivilege("PTP_UPDATE")}>
      <StatusTag
        style={getPTPStatusConfig(data?.status).style}
        text={getPTPStatusConfig(data?.status).label}
      />
    </HideWrapper>
  ) : null;
  const ActionArea = (
    <>
      {isLoading ? (
        <SkeletonInput />
      ) : (
        <Flex align="center">
          {StatusSelect}
          {Status}
          {DeleteButton}
          {actionButton?.map((action) => (
            <Fragment key={action.id}>{action.component}</Fragment>
          ))}
        </Flex>
      )}
    </>
  );
  const Header = header ? (
    header
  ) : (
    <FormHeader
      mode="drawer"
      onClose={handleClose}
      title={HeaderTitle}
      extra={ActionArea}
    />
  );

  const Footer = footer ? footer : null;
  // const Comments = <Comment type='DRAWER' activity="ptps" id={id} />;
  function getFooterStyle() {
    return {
      padding: commentOpen ? 0 : "var(--space-12) var(--space-24)",
      backgroundColor: "var(--gray-2)",
    };
  }

  const footerStyle = getFooterStyle();

  return (
    <ScrollDrawer
      title={Header}
      open={open}
      footer={Footer}
      width={640}
      getContainer={false}
      mask={true}
      maskClosable
      onClose={handleClose}
      styles={{
        wrapper: {
          background: "var(--gray-1)",
          boxShadow: "var(--shadow-4)",
          height: "var(--drawer-height)",
        },
         header: {
          padding: 0,
        },
        footer: footerStyle,
        mask: {
          position: "fixed",
          inset: 0,
        },
      }}
      push={false}
      destroyOnClose
      zIndex={DRAWER_ZINDEX}
    >
      <PTPDetailsComp data={data} loading={isLoading} />
    </ScrollDrawer>
  );
}

export { PTPDetailsView };
